function _class_call_check(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _create_class(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { TimedMetadataType } from '../../core/player/player-engine-item';
import { AdSignalingEventKey } from '../bolt-ons/ad-breaks-provider/ad-break-adapters/manifest-metadata/metadata-ad-breaks-adapter';
import { sdkLogger } from '../../logger';
import { stringOrUint8ToString } from '../../utils/utf8Utils';
// This class has two responsibilities:
// 1) Bulk emitting all X-AD-CREATIVE-SIGNALING data found in the manifest when a stream is first loaded
// 2) Workaround to throttle the ID3 data coming from Safari
// https://stackoverflow.com/questions/30330568/is-it-possible-to-get-information-from-id3-tag-metadata-embedded-in-hls-stream-i
//
// This workaround already exists in Shaka player and can be observed via the 'metadata' event
// https://github.com/google/shaka-player/blob/3670996269b0c339f93cb0134fa427d745a24289/lib/player.js#L2400
//
// Unfortunately it's not usable for us without performance concerns since
// each event is raised individually and each mp4 fragment contains metadata, so the number of
// events that need to be parsed will increase for each fragment after the sle-end point.
//
// Instead we handle this CVSDK side and only look at the last active cue to avoid parsing data for every fragment.
export var SafariMetadataManager = /*#__PURE__*/ function() {
    "use strict";
    function SafariMetadataManager(videoElement, notifyTimedMetadataReceived, notifyBulkTimedMetadataReceived) {
        var _this = this;
        _class_call_check(this, SafariMetadataManager);
        _define_property(this, "videoElement", void 0);
        _define_property(this, "notifyTimedMetadataReceived", void 0);
        _define_property(this, "notifyBulkTimedMetadataReceived", void 0);
        _define_property(this, "_logger", void 0);
        _define_property(this, "handleAddTrack", void 0);
        /**
     * Collects all metadata that needs to be bulk emitted (e.g. ad creative signaling)
     */ _define_property(this, "handleLoadedData", void 0);
        _define_property(this, "handleCueChange", void 0);
        this.videoElement = videoElement;
        this.notifyTimedMetadataReceived = notifyTimedMetadataReceived;
        this.notifyBulkTimedMetadataReceived = notifyBulkTimedMetadataReceived;
        this.handleAddTrack = function(addTrackEvent) {
            var track = addTrackEvent.track;
            if (track.kind !== 'metadata') {
                return;
            }
            track.mode = 'hidden';
            track.addEventListener('cuechange', function() {
                return _this.handleCueChange(track);
            });
        };
        this.handleLoadedData = function() {
            var dateRanges = [];
            var totalStartTime = -1;
            var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                for(var _iterator = _this.videoElement.textTracks[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                    var textTrack = _step.value;
                    if (!textTrack.cues) {
                        continue;
                    }
                    var processedDataCueIds = new Set();
                    var _iteratorNormalCompletion1 = true, _didIteratorError1 = false, _iteratorError1 = undefined;
                    try {
                        for(var _iterator1 = textTrack.cues[Symbol.iterator](), _step1; !(_iteratorNormalCompletion1 = (_step1 = _iterator1.next()).done); _iteratorNormalCompletion1 = true){
                            var cue = _step1.value;
                            var dataCue = cue;
                            if (dataCue.value && dataCue.value.key === 'X-AD-CREATIVE-SIGNALING') {
                                var dateRange = _this.convertAdSignalingDataCueToHlsDateRange(dataCue);
                                if (!dateRange) {
                                    _this._logger.error('An ad signaling data cue was received with missing data', dataCue);
                                    continue;
                                } else if (processedDataCueIds.has(dateRange.id)) {
                                    _this._logger.warn('A duplicate ad signaling data cue was received', dataCue);
                                    continue;
                                }
                                processedDataCueIds.add(dateRange.id);
                                if (totalStartTime === -1) {
                                    // The first time that was encountered
                                    totalStartTime = dateRange.playerTime;
                                } else {
                                    // All subsequent times
                                    totalStartTime += dateRange.playerTime;
                                }
                                dateRanges.push(dateRange);
                            }
                        }
                    } catch (err) {
                        _didIteratorError1 = true;
                        _iteratorError1 = err;
                    } finally{
                        try {
                            if (!_iteratorNormalCompletion1 && _iterator1.return != null) {
                                _iterator1.return();
                            }
                        } finally{
                            if (_didIteratorError1) {
                                throw _iteratorError1;
                            }
                        }
                    }
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
            if (totalStartTime === 0) {
                _this._logger.warn('Warning, Safari reported an invalid startTime of zero for all ads within the content. Timed metadata will be notified with malformed start times and durations.');
            }
            if (dateRanges.length > 0) {
                _this.notifyBulkTimedMetadataReceived(dateRanges);
            }
        };
        this.handleCueChange = function(track) {
            var activeCues = track.activeCues;
            if (!activeCues || !activeCues.length) {
                return;
            }
            var cuesToRemove = [];
            var id3Events = [];
            for(var i = 0; i < activeCues.length; i++){
                var cue = activeCues[i];
                var id3Event = _this.createDataCueEvent(cue);
                id3Events.push(id3Event);
                cuesToRemove.push(cue);
            }
            cuesToRemove.forEach(function(cue) {
                return track.removeCue(cue);
            });
            id3Events.forEach(function(id3Event) {
                return _this.notifyTimedMetadataReceived(id3Event);
            });
        };
        this._logger = sdkLogger.withContext('SafariMetadataManager');
    }
    _create_class(SafariMetadataManager, [
        {
            key: "addListeners",
            value: function addListeners() {
                this.videoElement.textTracks.addEventListener('addtrack', this.handleAddTrack);
                this.videoElement.addEventListener('loadeddata', this.handleLoadedData);
            }
        },
        {
            key: "removeListeners",
            value: function removeListeners() {
                this.videoElement.textTracks.removeEventListener('addtrack', this.handleAddTrack);
                this.videoElement.removeEventListener('loadeddata', this.handleLoadedData);
            }
        },
        {
            key: "createDataCueEvent",
            value: function createDataCueEvent(cue) {
                var _cue_value, _cue_value1, _cue_value2;
                var peiDataCue = {
                    type: TimedMetadataType.DataCue,
                    data: (_cue_value = cue.value) === null || _cue_value === void 0 ? void 0 : _cue_value.data,
                    info: (_cue_value1 = cue.value) === null || _cue_value1 === void 0 ? void 0 : _cue_value1.info,
                    key: (_cue_value2 = cue.value) === null || _cue_value2 === void 0 ? void 0 : _cue_value2.key,
                    format: cue.type,
                    presentationTime: cue.startTime
                };
                return peiDataCue;
            }
        },
        {
            key: "convertAdSignalingDataCueToHlsDateRange",
            value: function convertAdSignalingDataCueToHlsDateRange(cue) {
                var _cue_value;
                if (!((_cue_value = cue.value) === null || _cue_value === void 0 ? void 0 : _cue_value.data)) {
                    return;
                }
                // We convert to HlsDateRange here in the hopes that Safari will see a day where HlsDateRange tags are properly surfaced by the native player
                var b64Data = stringOrUint8ToString(cue.value.data);
                var cueData = JSON.parse(atob(b64Data));
                var absoluteStartTime = cueData.identifiers[0].start_time_in_seconds || cue.startTime;
                return {
                    type: TimedMetadataType.HlsDaterange,
                    id: cueData.identifiers[0].ad_position,
                    clientAttributes: cueData.identifiers[0],
                    // Favor start_time_in_seconds when possible
                    playerTime: absoluteStartTime,
                    // Always use difference between start and end, regardless of absolute start time
                    duration: cue.endTime - cue.startTime,
                    class: AdSignalingEventKey.HlsDaterange
                };
            }
        }
    ]);
    return SafariMetadataManager;
}();

//# sourceMappingURL=safari-metadata-manager.js.map